import { Layout } from "../../components/layout/Layout";
import { PageTransition } from "../../components/layout/PageTransition";
import { SEO } from "../../components/SEO";
import { Button } from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import {
  Zap,
  Shield,
  Globe,
  BarChart3,
  Users,
  Workflow,
  ArrowRight,
  CheckCircle2,
  Layers,
  Lock,
  Cpu,
  Cloud,
} from "lucide-react";
import styled from "styled-components";
import { Link } from "react-router-dom";

const Section = styled.section`
  padding: 96px 16px;
  position: relative;
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const BackgroundBlobs = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(128px);
  animation: pulse-slow 4s ease-in-out infinite;

  &.blob-1 {
    top: 80px;
    right: 25%;
    width: 500px;
    height: 500px;
    background: ${({ theme }) => `${theme.colors.light?.primary || "hsl(258, 90%, 58%)"}20`};
  }

  &.blob-2 {
    bottom: 80px;
    left: 25%;
    width: 400px;
    height: 400px;
    background: ${({ theme }) => `${theme.colors.light?.accent || "hsl(16, 90%, 58%)"}20`};
    animation-delay: 2s;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
`;

const HeroContent = styled.div`
  max-width: 768px;
  margin: 0 auto;
  text-align: center;
`;

const BadgeWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h1};
  font-weight: 700;
  line-height: 1.1;
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const GradientText = styled.span`
  background: ${({ theme }) => theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const MainFeaturesSection = styled.section`
  padding: 96px 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const FeatureRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
  margin-bottom: 96px;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    flex-direction: ${({ $reverse }) => ($reverse ? "row-reverse" : "row")};
    gap: 80px;
    align-items: center;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const IconBox = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: ${({ $gradient }) => $gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: ${({ theme }) => theme.shadows.soft};
  color: white;
`;

const FeatureTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: clamp(1.875rem, 3vw, 2.25rem);
  font-weight: 700;
  margin: 0 0 16px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const FeatureDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0 0 32px 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 32px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const CheckIcon = styled(CheckCircle2)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.light?.primary || theme.colors.primary.light};
  flex-shrink: 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.primary || theme.colors.primary.light};
  }
`;

const CheckCircle = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ theme }) => `${theme.colors.light?.primary || theme.colors.primary.light}20`};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const VisualBox = styled.div`
  flex: 1;
  width: 100%;
`;

const VisualCard = styled.div`
  aspect-ratio: 1;
  max-width: 384px;
  margin: 0 auto;
  border-radius: 24px;
  background: ${({ theme }) => theme.colors.glass?.light?.background || "hsla(0, 0%, 100%, 0.8)"};
  backdrop-filter: blur(20px);
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};
  padding: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  body.dark & {
    background: ${({ theme }) => theme.colors.glass?.dark?.background || "hsla(240, 15%, 10%, 0.8)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }
`;

const VisualIcon = styled.div`
  width: 128px;
  height: 128px;
  border-radius: 24px;
  background: ${({ $gradient }) => $gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => theme.shadows.glow};
  animation: float 6s ease-in-out infinite;
  color: white;
`;

const AdditionalFeaturesSection = styled.section`
  padding: 96px 16px;
  background: ${({ theme }) => theme.colors.light?.muted?.DEFAULT || "hsl(240, 10%, 96%)"}30;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.muted?.DEFAULT || "hsl(240, 15%, 15%)"}30;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const SectionHeader = styled.div`
  max-width: 768px;
  margin: 0 auto 64px;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: clamp(1.875rem, 3vw, 2.25rem);
  font-weight: 700;
  margin: 0 0 16px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const SectionDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const FeatureCard = styled.div`
  padding: 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};
  transition: all 0.5s ease-out;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const CardIcon = styled.div`
  color: ${({ theme }) => theme.colors.light?.primary || theme.colors.primary.light};
  margin-bottom: 16px;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.primary || theme.colors.primary.light};
  }
`;

const CardTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-weight: 600;
  margin: 0 0 8px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const CardDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const CTASection = styled.section`
  padding: 96px 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const CTAWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  justify-content: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    flex-direction: row;
  }
`;

const mainFeatures = [
  {
    icon: Zap,
    title: "Lightning Performance",
    description: "Our infrastructure is optimized for speed. Experience sub-100ms response times globally with our edge-deployed architecture.",
    benefits: [
      "Sub-100ms response times",
      "99.99% uptime SLA",
      "Automatic scaling",
      "CDN-powered delivery",
    ],
    gradient: "linear-gradient(to bottom right, hsl(48, 96%, 53%), hsl(25, 95%, 53%))",
  },
  {
    icon: Shield,
    title: "Enterprise Security",
    description: "Bank-grade security with SOC 2 Type II compliance, end-to-end encryption, and advanced threat protection.",
    benefits: [
      "SOC 2 Type II certified",
      "End-to-end encryption",
      "SSO & SAML support",
      "Audit logging",
    ],
    gradient: "linear-gradient(to bottom right, hsl(142, 76%, 36%), hsl(160, 84%, 39%))",
  },
  {
    icon: Workflow,
    title: "Smart Automation",
    description: "Build powerful workflows without code. Our AI learns your patterns and suggests optimizations automatically.",
    benefits: [
      "Visual workflow builder",
      "AI-powered suggestions",
      "Custom triggers",
      "100+ integrations",
    ],
    gradient: "linear-gradient(to bottom right, hsl(262, 83%, 58%), hsl(330, 81%, 60%))",
  },
];

const additionalFeatures = [
  { icon: Globe, title: "Global CDN", description: "Content delivered from 200+ edge locations" },
  { icon: BarChart3, title: "Real-time Analytics", description: "Track every metric that matters" },
  { icon: Users, title: "Team Workspaces", description: "Collaborate seamlessly across teams" },
  { icon: Layers, title: "Version Control", description: "Never lose work with automatic versioning" },
  { icon: Lock, title: "Role-based Access", description: "Granular permissions for every user" },
  { icon: Cpu, title: "API First", description: "Build on top of our powerful REST API" },
  { icon: Cloud, title: "Cloud Native", description: "Deploy anywhere with containers" },
  { icon: CheckCircle2, title: "Quality Assurance", description: "Automated testing and monitoring" },
];

export default function Features() {
  return (
    <Layout>
      <SEO
        title="Features"
        description="Discover all the powerful features that make Kliq CRM the complete platform for modern teams."
        keywords="features, automation, security, performance, analytics, collaboration"
        hreflang
      />
      <PageTransition>
        {/* Hero */}
        <Section>
          <BackgroundBlobs>
            <Blob className="blob-1" />
            <Blob className="blob-2" />
          </BackgroundBlobs>
          <Container>
            <HeroContent>
              <BadgeWrapper>
                <Badge $variant="primary">Features</Badge>
              </BadgeWrapper>
              <Title>
                Powerful features for <GradientText>modern teams</GradientText>
              </Title>
              <Description>
                Everything you need to streamline your workflow, boost productivity, and scale your operations.
              </Description>
            </HeroContent>
          </Container>
        </Section>

        {/* Main Features */}
        <MainFeaturesSection>
          <Container>
            {mainFeatures.map((feature, index) => (
              <FeatureRow key={feature.title} $reverse={index % 2 !== 0}>
                <FeatureContent>
                  <IconBox $gradient={feature.gradient}>
                    <feature.icon size={32} />
                  </IconBox>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureDescription>{feature.description}</FeatureDescription>
                  <BenefitsList>
                    {feature.benefits.map((benefit) => (
                      <BenefitItem key={benefit}>
                        <CheckCircle>
                          <CheckIcon size={12} />
                        </CheckCircle>
                        <span>{benefit}</span>
                      </BenefitItem>
                    ))}
                  </BenefitsList>
                  <Link to="/contact" style={{ textDecoration: "none" }}>
                    <Button variant="outline" size="lg">
                      Learn more
                      <ArrowRight size={16} />
                    </Button>
                  </Link>
                </FeatureContent>
                <VisualBox>
                  <VisualCard>
                    <VisualIcon $gradient={feature.gradient}>
                      <feature.icon size={64} />
                    </VisualIcon>
                  </VisualCard>
                </VisualBox>
              </FeatureRow>
            ))}
          </Container>
        </MainFeaturesSection>

        {/* Additional Features Grid */}
        <AdditionalFeaturesSection>
          <Container>
            <SectionHeader>
              <SectionTitle>And so much more</SectionTitle>
              <SectionDescription>
                Discover all the tools that make Kliq CRM the complete platform for your team.
              </SectionDescription>
            </SectionHeader>

            <FeaturesGrid>
              {additionalFeatures.map((feature) => (
                <FeatureCard key={feature.title}>
                  <CardIcon>
                    <feature.icon size={32} />
                  </CardIcon>
                  <CardTitle>{feature.title}</CardTitle>
                  <CardDescription>{feature.description}</CardDescription>
                </FeatureCard>
              ))}
            </FeaturesGrid>
          </Container>
        </AdditionalFeaturesSection>

        {/* CTA */}
        <CTASection>
          <Container>
            <HeroContent>
              <SectionTitle>Ready to get started?</SectionTitle>
              <Description style={{ marginBottom: "32px" }}>
                Join thousands of teams already using Kliq CRM to power their work.
              </Description>
              <CTAWrapper>
                <Link to="/signup" style={{ textDecoration: "none" }}>
                  <Button variant="hero" size="lg">
                    Start Free Trial
                    <ArrowRight size={20} />
                  </Button>
                </Link>
                <Link to="/contact" style={{ textDecoration: "none" }}>
                  <Button variant="outline" size="lg">
                    Talk to Sales
                  </Button>
                </Link>
              </CTAWrapper>
            </HeroContent>
          </Container>
        </CTASection>
      </PageTransition>
    </Layout>
  );
}
